import type { SetupRingPage, SetupRingProgress } from '@/components/setup/SetupRing/SetupRing.vue'
import {
  RUN_TIMED_OUT_ERROR_CODE,
  type RunEntry,
  type RunEntryStep,
  type SetupRun,
} from '@/types/setup-run'

/** i18n keys by run error code: the line under a failed run. */
export type SetupRunErrorMessages = Readonly<Record<string, string>>

/** i18n keys by run stage: the line under the ring while the run is on that stage. */
export type SetupRunStageLines = Readonly<Record<string, string>>

/** The error codes every step's run can end with, whatever the step. */
const SETUP_RUN_ERROR_MESSAGES: SetupRunErrorMessages = {
  [RUN_TIMED_OUT_ERROR_CODE]: 'setup.common.runErrors.timedOut',
}

/** The line under a failed run whose error code nobody names. */
export const SETUP_RUN_GENERIC_ERROR_KEY = 'setup.common.runErrors.generic'

const ENTRY_STATES: Record<RunEntryStep, SetupRingPage['state']> = {
  queued: 'todo',
  running: 'live',
  done: 'done',
  failed: 'failed',
}

/** The entries of a run, in the shape the ring lists them. */
export function runEntryPages<Entry extends RunEntry>(
  run: SetupRun<string, Entry>,
  label: (entry: Entry) => string,
): SetupRingPage[] {
  return run.entries.map((entry) => ({ label: label(entry), state: ENTRY_STATES[entry.step] }))
}

/** How many entries have settled, or `undefined` before a run has any. */
export function runProgress(run: SetupRun): SetupRingProgress | undefined {
  if (run.entries.length === 0) return undefined
  const done = run.entries.filter(
    (entry) => entry.step === 'done' || entry.step === 'failed',
  ).length
  return { done, total: run.entries.length }
}

/** The entry a run is on right now, or `undefined` between entries. */
export function runLiveEntry<Entry extends RunEntry>(
  run: SetupRun<string, Entry>,
): Entry | undefined {
  return run.entries.find((entry) => entry.step === 'running')
}

/** The i18n key for the line under a failed run: the step's key for the code, else the shared key, else {@link SETUP_RUN_GENERIC_ERROR_KEY}. */
export function runFailureMessageKey(run: SetupRun, messages: SetupRunErrorMessages): string {
  const code = run.errorCode
  if (code === undefined) return SETUP_RUN_GENERIC_ERROR_KEY
  if (Object.hasOwn(messages, code)) return messages[code] ?? SETUP_RUN_GENERIC_ERROR_KEY
  if (Object.hasOwn(SETUP_RUN_ERROR_MESSAGES, code)) {
    return SETUP_RUN_ERROR_MESSAGES[code] ?? SETUP_RUN_GENERIC_ERROR_KEY
  }
  return SETUP_RUN_GENERIC_ERROR_KEY
}

/** The i18n key for the line under the ring while a run works, or `undefined` for a stage that names its live entry instead. */
export function runStageLineKey(run: SetupRun, lines: SetupRunStageLines): string | undefined {
  return Object.hasOwn(lines, run.stage) ? lines[run.stage] : undefined
}
